import apiClient from '@/lib/api/apiClient'
import { getBrowserApiBaseUrl } from '@/config/env'

async function downloadServeFileBlob(path: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(path, { responseType: 'blob' })
  return response.data
}

/**
 * Normalize stream / serve URLs returned by the API for axios.
 * In Vite dev, absolute API URLs are rewritten to same-origin paths so the proxy
 * forwards the request (avoids CORS when opening Access Confirmation PDFs).
 */
export function resolveStreamFetchPath(streamUrl: string): string {
  const raw = streamUrl.trim()
  if (!raw) return raw

  try {
    const normalized =
      raw.startsWith('/') || /^https?:\/\//i.test(raw) ? raw : `/${raw}`
    const url = new URL(normalized, window.location.origin)
    let base = getBrowserApiBaseUrl().replace(/\/+$/, '')
    // Claims endpoints only — `/serve-file` and `/stream-file` keep their existing base.
    if (url.pathname.startsWith('/serve-file-claims')) {
      base = base.replace(/\/api$/, '')
    }
    return `${base}${url.pathname}${url.search}`
  } catch {
    return raw.startsWith('/') ? raw : `/${raw}`
  }
}

export async function openServeFileInNewTab(path: string): Promise<void> {
  const blob = await downloadServeFileBlob(resolveStreamFetchPath(path))
  const url = window.URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000)
}

/**
 * Same as {@link openServeFileInNewTab}, but claims the tab synchronously inside the
 * click handler so the browser pop-up blocker does not swallow it while the blob loads.
 */
export async function openServeFileInNewTabFromGesture(path: string): Promise<void> {
  const win = typeof window !== 'undefined' ? window.open('about:blank', '_blank') : null
  try {
    const blob = await downloadServeFileBlob(resolveStreamFetchPath(path))
    const url = window.URL.createObjectURL(blob)
    if (win && !win.closed) {
      win.location.href = url
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000)
  } catch (err) {
    if (win && !win.closed) win.close()
    throw err
  }
}

/** Blob download with `download` attribute — matches Angular policy-declaration popup. */
export async function triggerServeFileDownload(
  streamUrl: string,
  filename: string,
): Promise<void> {
  const blob = await downloadServeFileBlob(resolveStreamFetchPath(streamUrl))
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.trim() || 'terms-and-conditions.pdf'
  a.click()
  window.URL.revokeObjectURL(url)
}

/**
 * Angular `downloadClaimsFile` for list Statement column:
 * strip host from absolute S3 URLs, then encode for `?url=`.
 */
export function encodeServeFileClaimsPath(filepath: string): string {
  const s3Key = String(filepath ?? '')
    .trim()
    .replace(/^https?:\/\/[^/]+\/?/, '')
  return encodeURIComponent(s3Key)
}

/**
 * Angular list Statement download:
 * `/serve-file-claims/Statement/{applicationId}/?url={encodedS3Key}`
 */
export function buildStatementPath(
  applicationId: string | number,
  fileType: string,
  filepath: string,
): string {
  return `/serve-file-claims/${fileType}/${applicationId}/?url=${encodeServeFileClaimsPath(filepath)}`
}

/**
 * Angular `downloadStatement` (detail Open):
 * `/serve-file-claims/Statement/?url={encodedS3Key}` — no application id segment.
 */
export function buildStatementOpenPath(filepath: string, fileType = 'Statement'): string {
  return `/serve-file-claims/${fileType}/?url=${encodeServeFileClaimsPath(filepath)}`
}
